import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { MediaAsset } from '@/lib/blob-service';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { 
  Sparkles,
  TrendingUp,
  Zap,
  Clock,
  PlayCircle,
  Instagram,
  Youtube,
  ArrowRight,
  Gift,
  Play,
  X,
  PenLine,
  Rocket,
  Megaphone,
  Film,
  GraduationCap,
  Building2,
  Users,
  Mic,
} from 'lucide-react';
import { TikTokIcon } from '@/components/icons/TikTokIcon';
import { Badge } from '@/components/ui/badge';

// Update type definition
type Example = {
  prompt: string;
  title: string;
  description: string;
  video: MediaAsset;
  model: string;
};

const examples: Example[] = [
  {
    prompt: "A funny take on morning coffee addiction",
    title: "Coffee Shop Story",
    description: "A humorous video about coffee addiction and morning routines",
    video: {
      id: 'coffee-landing',
      type: 'video',
      blobUrl: 'https://bkmjmkkjkywolycz.public.blob.vercel-storage.com/videos/coffee-landing.mp4',
      fallbackUrl: '/videos/coffee-landing.mp4',
    },
    model: "Kling"
  },
  {
    prompt: "A brutalist concrete runway emerges from darkness, illuminated by harsh, directional lighting. Models move with deliberate, almost ritualistic movements, wearing avant-garde silhouettes in monochromatic blacks and greys. Their forms cast dramatic shadows against bare concrete walls. The camera glides smoothly, capturing both the architectural severity of the space and the fluid movement of the garments. Smoke drifts across the floor, creating an ethereal atmosphere that contrasts with the structural hardness. The collection embodies a post-apocalyptic elegance: draped fabrics, geometric cutouts, and architectural shapes that echo Rick Owens' design philosophy. The finale shows all models in formation, their silhouettes creating a living sculpture against the brutalist backdrop.",
    title: "Avant-Garde Fashion",
    description: "A brutalist fashion showcase with architectural influences",
    video: {
      id: 'landing_rick',
      type: 'video',
      blobUrl: 'https://bkmjmkkjkywolycz.public.blob.vercel-storage.com/videos/landing_rick.mp4',
      fallbackUrl: '/videos/landing_rick.mp4',
    },
    model: "Veo2"
  },
  {
    prompt: "A meditative journey through Tadao Ando's architectural masterpieces in Japan. The camera floats through the Church of Light in Osaka, where sunbeams pierce through the cruciform void, creating a dramatic play of light and shadow on raw concrete. Transitions flow seamlessly to Benesse House in Naoshima, following water elements that reflect sky and concrete. The focus is on the interplay between natural and man-made elements: circular openings frame the sky, geometric shadows move across textured concrete walls, and water features mirror the architecture in perfect symmetry. The movement is slow and contemplative, emphasizing the spiritual quality of the spaces. Natural sounds of water and wind complement the visual journey.",
    title: "Architectural Poetry",
    description: "A visual exploration of Tadao Ando's iconic spaces",
    video: {
      id: 'landing_ando_2',
      type: 'video',
      blobUrl: 'https://bkmjmkkjkywolycz.public.blob.vercel-storage.com/videos/landing_ando_2.mp4',
      fallbackUrl: '/videos/landing_ando_2.mp4',
    },
    model: "Kling"
  },
  {
    prompt: "Tadao Ando Designs in Osaka and Naoshima",
    title: "Tadao Ando's Designs",
    description: "Journey through Ando's masterpieces in Japan",
    video: {
      id: 'landing_ando',
      type: 'video',
      blobUrl: 'https://bkmjmkkjkywolycz.public.blob.vercel-storage.com/videos/landing_ando.mp4',
      fallbackUrl: '/videos/landing_ando.mp4',
    },
    model: "Kling"
  },
  {
    prompt: "Create a cinematic, introspective video of a creator standing in a minimalist room, speaking in an inner monologue. They're separated from the world by a glass wall. As they speak, we see subtle reflections of people on the other side—potential users, perhaps unaware of his presence. The creator questions whether his message is clear. Abstract visuals begin to appear: floating ideas, fragments of content, hints of creativity forming. The tone shifts from uncertainty to clarity. A glowing interface appears in front of him, representing his tool—SmartReel. He reaches toward it calmly. The figures on the other side pause, as if something just clicked. End with a fade to black and the SmartReel logo. Voiceover should be poetic and personal, ending with the line: 'Maybe they're not listening yet… but they'll feel it when they press play.'",
    title: "SmartReels Creative Vision",
    description: "An introspective journey through the creative process",
    video: {
      id: 'landing',
      type: 'video',
      blobUrl: 'https://bkmjmkkjkywolycz.public.blob.vercel-storage.com/videos/landing.mp4',
      fallbackUrl: '/videos/landing.mp4',
    },
    model: "Kling"
  }
];

// Switchable demos for the "How It Works" section — three prompt → video
// examples the visitor can flip between. All on the blob CDN so they carry
// audio in the click-through modal.
const HIW_BLOB = 'https://bkmjmkkjkywolycz.public.blob.vercel-storage.com/videos';
const howItWorksDemos = [
  {
    label: 'Fashion',
    idea: 'An avant-garde brutalist fashion runway',
    title: 'Avant-Garde Fashion, from a single prompt',
    blobUrl: `${HIW_BLOB}/landing_rick.mp4`,
    fallbackUrl: '/videos/landing_rick.mp4',
  },
  {
    label: 'Architecture',
    idea: 'A meditative journey through Naoshima Island',
    title: 'Architectural Poetry, from a single prompt',
    blobUrl: `${HIW_BLOB}/landing_ando_2.mp4`,
    fallbackUrl: '/videos/landing_ando_2.mp4',
  },
  {
    label: 'Everyday',
    idea: 'A funny take on morning coffee addiction',
    title: 'Coffee Shop Story, from a single prompt',
    blobUrl: `${HIW_BLOB}/coffee-landing.mp4`,
    fallbackUrl: '/videos/coffee-landing.mp4',
  },
];

// Who SmartReel is for — pulled from the Market section of the product doc.
const audiences = [
  {
    icon: PenLine,
    name: 'Thinkers & writers',
    blurb: 'Turn an X thread, essay, or Substack post into a Short — no editing.',
  },
  {
    icon: Rocket,
    name: 'Indie hackers',
    blurb: 'Build-in-public updates and product stories as quick videos.',
  },
  {
    icon: Megaphone,
    name: 'Content marketers',
    blurb: 'Fast explainers, launch teasers, and campaign assets on demand.',
  },
  {
    icon: Film,
    name: 'Cinematic storytellers',
    blurb: 'Aesthetic-first prompts: trailers, memory films, mood pieces.',
  },
  {
    icon: GraduationCap,
    name: 'Tech educators',
    blurb: 'Short, clear explainers that make abstract topics click.',
  },
  {
    icon: Building2,
    name: 'Real estate',
    blurb: 'Drop in a listing and turn it into a polished property tour.',
  },
  {
    icon: Users,
    name: 'Teams & internal comms',
    blurb: 'Async updates and presentations without a production crew.',
  },
  {
    icon: Sparkles,
    name: '& everyone else',
    blurb: 'Whatever you can describe, SmartReel can make. It’s up to you.',
  },
];

// The SmartReel advantage — core differentiators, unified indigo styling.
const advantages = [
  {
    icon: Sparkles,
    title: 'Creative Freedom',
    blurb: 'Your vision, your way. No templates or creative limits.',
  },
  {
    icon: TrendingUp,
    title: 'Platform Ready',
    blurb: 'Perfect for every platform, automatically optimised.',
  },
  {
    icon: Zap,
    title: 'Instant Scale',
    blurb: 'Create multiple versions in minutes, not hours.',
  },
  {
    icon: Mic,
    title: 'Pro Voice',
    blurb: 'Studio-quality narration without the studio.',
  },
];

export function LandingPage() {
  const [showVideo, setShowVideo] = useState(false);
  const [activeDemo, setActiveDemo] = useState(0);
  const demo = howItWorksDemos[activeDemo];
  const [showExamples, setShowExamples] = useState(false);
  const [selectedExample, setSelectedExample] = useState<Example | null>(null);
  const [showFloatingCTA, setShowFloatingCTA] = useState(false);
  const footerRef = useRef<HTMLElement>(null);
  const heroRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const footerObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowFloatingCTA(false);
        } else {
          // When footer is not visible, rely on hero observer for CTA visibility
          const heroEntry = heroObserver.takeRecords()[0];
          if (heroEntry) {
            setShowFloatingCTA(!heroEntry.isIntersecting && heroEntry.intersectionRatio < 0.8);
          }
        }
      },
      {
        threshold: 0.8,
      }
    );

    const heroObserver = new IntersectionObserver(
      ([entry]) => {
        // Show CTA when hero section is 20% out of view and footer is not visible
        const footerEntry = footerObserver.takeRecords()[0];
        if (!footerEntry?.isIntersecting) {
        setShowFloatingCTA(!entry.isIntersecting && entry.intersectionRatio < 0.8);
        }
      },
      {
        threshold: [0.2, 0.8],
        rootMargin: '-20% 0px 0px 0px'
      }
    );

    if (footerRef.current) {
      footerObserver.observe(footerRef.current);
    }
    if (heroRef.current) {
      heroObserver.observe(heroRef.current);
    }

    return () => {
      footerObserver.disconnect();
      heroObserver.disconnect();
    };
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section ref={heroRef} className="flex-1 flex flex-col items-center justify-center min-h-[85vh] sm:min-h-0 py-12 sm:py-10 md:py-20 px-0 sm:px-5 md:px-4 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
        <div className="flex flex-col items-center text-center w-full max-w-4xl mx-auto px-5">
          <h1 className="text-[2.25rem] sm:text-4xl md:text-6xl font-bold tracking-tight leading-[1.15]">
            Turn Thoughts Into{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 to-indigo-700">
              Videos Instantly
            </span>
          </h1>
          
          <p className="mt-4 sm:mt-6 text-[1.0625rem] sm:text-lg md:text-xl text-muted-foreground/90 max-w-2xl leading-relaxed">
            Skip the equipment, crew, and editing. Just type your idea and let AI create your video.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 md:gap-8 mt-10 sm:mt-12 w-full max-w-3xl px-0 sm:px-0">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-white via-white to-white border shadow-sm hover:shadow-md transition-all duration-300"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative p-4 sm:p-5 md:p-6 flex flex-row md:flex-col items-center gap-4 md:gap-0">
                <div className="shrink-0 bg-gradient-to-br from-indigo-100 to-indigo-50 p-2.5 sm:p-3 rounded-xl md:mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Zap className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-600" />
                </div>
                <div className="text-left md:text-center">
                  <div className="text-xl sm:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-indigo-800">&lt; 5 minutes</div>
                  <div className="text-sm text-muted-foreground/80 mt-0.5 sm:mt-1.5 group-hover:text-muted-foreground transition-colors duration-300">From idea to video</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-white via-white to-white border shadow-sm hover:shadow-md transition-all duration-300"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative p-4 sm:p-5 md:p-6 flex flex-row md:flex-col items-center gap-4 md:gap-0">
                <div className="shrink-0 bg-gradient-to-br from-emerald-100 to-emerald-50 p-2.5 sm:p-3 rounded-xl md:mb-4 group-hover:scale-110 transition-transform duration-300">
                  <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
                </div>
                <div className="text-left md:text-center">
                  <div className="text-xl sm:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 to-emerald-800">$5,000+</div>
                  <div className="text-sm text-muted-foreground/80 mt-0.5 sm:mt-1.5 group-hover:text-muted-foreground transition-colors duration-300">Save on equipment</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-white via-white to-white border shadow-sm hover:shadow-md transition-all duration-300"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-violet-50/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="relative p-4 sm:p-5 md:p-6 flex flex-row md:flex-col items-center gap-4 md:gap-0">
                <div className="shrink-0 bg-gradient-to-br from-violet-100 to-violet-50 p-2.5 sm:p-3 rounded-xl md:mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-violet-600" />
                </div>
                <div className="text-left md:text-center">
                  <div className="text-xl sm:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-violet-800">5+ hours</div>
                  <div className="text-sm text-muted-foreground/80 mt-0.5 sm:mt-1.5 group-hover:text-muted-foreground transition-colors duration-300">Replace editing time</div>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-10 sm:mt-12 w-full sm:w-auto px-0 sm:px-0">
            <Button
              size="lg"
              className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white px-6 sm:px-8 py-6 rounded-xl flex items-center justify-center gap-2"
              onClick={() => navigate('/auth')}
            >
              <Sparkles className="h-5 w-5" />
              <span className="font-medium">Start Free • Get 500 Credits</span>
              </Button>
            
            <Button
              variant="outline"
              size="lg"
              className="w-full sm:w-auto px-6 sm:px-8 py-6 rounded-xl flex items-center justify-center"
              onClick={() => navigate('/showcase')}
            >
              <Play className="h-5 w-5 mr-2" />
              See Examples
              </Button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 sm:py-16 md:py-20 bg-white overflow-x-hidden">
        <div className="container mx-auto px-4 sm:px-5 md:px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-12 sm:mb-14 md:mb-12"
          >
            <h2 className="text-[1.75rem] sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">How It Works</h2>
            <p className="text-[1.0625rem] sm:text-base md:text-xl text-gray-600 max-w-lg mx-auto px-4 sm:px-0 leading-relaxed">
              Create engaging content in three simple steps
            </p>
          </motion.div>

          {/* Demo switcher — flip between three prompt → video examples */}
          <div className="mb-10 flex justify-center px-4">
            <div className="inline-flex w-full max-w-xs rounded-full border border-gray-200 bg-gray-50 p-1 sm:w-auto sm:max-w-none">
              {howItWorksDemos.map((d, i) => (
                <button
                  key={d.label}
                  type="button"
                  onClick={() => setActiveDemo(i)}
                  className={`flex-1 whitespace-nowrap rounded-full px-3 py-1.5 text-[13px] font-medium transition-colors sm:flex-none sm:px-4 sm:text-sm ${
                    i === activeDemo
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-900'
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-10 sm:gap-14 md:gap-12 items-start md:items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-8 sm:space-y-10 md:space-y-8 px-2 sm:px-0"
            >
              <Step
                number={1}
                title="Type Your Idea"
                description={
                  <div className="space-y-2">
                    <div className="bg-white/80 p-3 sm:p-2 rounded-lg border text-[15px] sm:text-sm font-mono text-gray-600 shadow-sm">
                      "{demo.idea}"
                      <div className="h-4 w-0.5 bg-indigo-600 animate-pulse inline-block ml-0.5 align-middle" />
                    </div>
                  </div>
                }
              />
              <Step
                number={2}
                title="AI Creates Your Video"
                description={
                  <div className="space-y-2 sm:space-y-1 text-[15px] sm:text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-indigo-600/10 flex items-center justify-center">
                        <span className="text-indigo-600 text-sm">✓</span>
                      </div>
                      Professional visuals
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-indigo-600/10 flex items-center justify-center">
                        <span className="text-indigo-600 text-sm">✓</span>
                      </div>
                      Natural voiceover
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-full bg-indigo-600/10 flex items-center justify-center">
                        <span className="text-indigo-600 text-sm">✓</span>
                      </div>
                      Perfect timing
                    </div>
                  </div>
                }
              />
              <Step
                number={3}
                title="Review & Share"
                description={
                  <div className="flex items-center gap-3 text-[15px] sm:text-sm text-gray-600">
                    <div className="flex -space-x-2">
                      <div className="w-7 h-7 rounded-full bg-black flex items-center justify-center ring-2 ring-white shadow-sm">
                        <TikTokIcon className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-600 to-orange-600 flex items-center justify-center ring-2 ring-white shadow-sm">
                        <Instagram className="w-3.5 h-3.5 text-white" />
                      </div>
                      <div className="w-7 h-7 rounded-full bg-red-600 flex items-center justify-center ring-2 ring-white shadow-sm">
                        <Youtube className="w-3.5 h-3.5 text-white" />
                      </div>
                    </div>
                    Share directly to your favorite platforms
                  </div>
                }
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="relative aspect-video rounded-lg overflow-hidden shadow-lg cursor-pointer group"
              onClick={() => setShowVideo(true)}
            >
              <video
                key={activeDemo}
                src={demo.blobUrl}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                onError={(e) => {
                  const video = e.currentTarget;
                  if (video.src !== demo.fallbackUrl) {
                    video.src = demo.fallbackUrl;
                  }
                }}
              />
              <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <PlayCircle className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 text-white drop-shadow-lg" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-16 sm:py-16 md:py-20 bg-gray-50 border-t border-gray-100">
        <div className="container mx-auto max-w-5xl px-4 sm:px-5 md:px-4">
          <div className="text-center mb-12 sm:mb-14">
            <h2 className="text-[1.75rem] sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
              Who it's for
            </h2>
            <p className="text-[1.0625rem] sm:text-base md:text-lg text-gray-600 max-w-xl mx-auto leading-relaxed">
              Built for thinkers who already write and want short-form reach — and
              anyone with an idea worth watching.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {audiences.map(a => (
              <div
                key={a.name}
                className="rounded-xl border border-gray-200 bg-white p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-sm"
              >
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                  <a.icon className="h-5 w-5" />
                </div>
                <h3 className="text-[15px] font-semibold text-gray-900">{a.name}</h3>
                <p className="mt-1 text-sm leading-relaxed text-gray-500">{a.blurb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coffee Video Modal */}
      <Dialog open={showVideo} onOpenChange={setShowVideo}>
        <DialogContent className="max-w-4xl p-0">
          <DialogHeader>
            <DialogTitle className="text-2xl p-6 pb-0">{demo.title}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video">
            <video
              className="w-full h-full object-cover"
              src={demo.blobUrl}
              preload="metadata"
              controls
              autoPlay
              loop
              onError={(e) => {
                const video = e.currentTarget;
                if (video.src !== demo.fallbackUrl) {
                  video.src = demo.fallbackUrl;
                }
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Examples Modal */}
      <Dialog open={showExamples} onOpenChange={setShowExamples}>
        <DialogContent className="max-w-4xl p-0 max-h-[90vh] flex flex-col">
          <div className="absolute top-4 right-4 z-50">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full bg-white/90 hover:bg-white/95 transition-colors"
              onClick={() => {
                if (selectedExample) {
                  setSelectedExample(null);
                } else {
                  setShowExamples(false);
                }
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {!selectedExample ? (
            <div className="p-6 space-y-4 overflow-y-auto">
              <DialogHeader className="sticky top-0 bg-background z-10 pb-4 w-full">
                <div className="w-full pr-8 bg-white -mt-6 -mx-6 px-6 pt-6">
                  <DialogTitle className="text-2xl">From Prompt to Video</DialogTitle>
                  <DialogDescription>
                    See how different prompts transform into engaging videos
                  </DialogDescription>
                </div>
              </DialogHeader>

              <div className="grid grid-cols-1 gap-4">
                {examples.map((example) => (
                  <div
                    key={example.title}
                    className="group relative rounded-lg overflow-hidden bg-black/5 cursor-pointer hover:bg-black/10 transition-all border"
                    onClick={() => setSelectedExample(example)}
                  >
                    <div className="grid md:grid-cols-[1fr,auto] gap-4 p-4">
                      <div className="space-y-3">
                        <div className="bg-white/80 p-3 rounded-lg border text-sm font-mono text-gray-600 shadow-sm">
                          "{example.prompt.length > 100 ? example.prompt.slice(0, 100) + '...' : example.prompt}"
                        </div>
                        <div>
                          <h3 className="font-medium text-lg">{example.title}</h3>
                          <p className="text-sm text-muted-foreground">{example.description}</p>
                        </div>
                      </div>
                      <div className="relative aspect-video w-full md:w-[200px] rounded overflow-hidden">
                        <video
                          src={example.video.blobUrl}
                          className="w-full h-full object-cover"
                          preload="metadata"
                          muted
                          loop
                          playsInline
                          onError={(e) => {
                            const video = e.currentTarget;
                            if (video.src !== example.video.fallbackUrl) {
                              video.src = example.video.fallbackUrl;
                            }
                          }}
                          onMouseOver={(e) => e.currentTarget.play()}
                          onMouseOut={(e) => {
                            e.currentTarget.pause();
                            e.currentTarget.currentTime = 0;
                          }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/20">
                          <Play className="h-8 w-8 text-white" />
                        </div>
                        <div className="absolute bottom-2 right-2">
                          <Badge variant="outline" className="bg-white/90">{example.model}</Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col max-h-[90vh]">
              <div className="aspect-video flex-shrink-0">
                <video
                  className="w-full h-full object-cover"
                  src={selectedExample.video.blobUrl}
                  preload="metadata"
                  controls
                  autoPlay
                  loop
                  onError={(e) => {
                    const video = e.currentTarget;
                    if (video.src !== selectedExample.video.fallbackUrl) {
                      video.src = selectedExample.video.fallbackUrl;
                    }
                  }}
                />
              </div>
              <div className="p-6 space-y-4 overflow-y-auto">
                <div className="bg-muted/50 p-3 rounded-lg border text-sm font-mono">
                  "{selectedExample.prompt}"
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-medium">{selectedExample.title}</h3>
                    <Badge variant="outline">{selectedExample.model}</Badge>
                  </div>
                  <p className="text-muted-foreground">{selectedExample.description}</p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Capabilities Comparison Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-gray-50 to-white overflow-x-hidden">
        <div className="container mx-auto px-4 sm:px-5 md:px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-8 md:mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4">Traditional vs SmartReel</h2>
            <p className="text-base md:text-xl text-gray-600 max-w-lg mx-auto">
              Skip the complexity, keep the quality
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto">
            {/* Traditional Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="relative overflow-hidden rounded-xl bg-white p-5 sm:p-8 shadow-sm border"
            >
              <div className="absolute top-0 right-0 w-[200px] h-[200px] bg-red-50 rounded-full blur-3xl -mr-20 -mt-20 opacity-30" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-4 sm:mb-8">
                  <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-red-500" />
                  <h3 className="text-base sm:text-xl font-semibold text-gray-900">Traditional</h3>
                </div>
                <ul className="space-y-3 sm:space-y-6">
                  <li>
                    <div className="flex items-baseline gap-2 sm:gap-3">
                      <div className="text-xl sm:text-3xl font-bold text-gray-900">$5,000+</div>
                      <div className="text-gray-500 text-xs sm:text-sm">Equipment & Hiring</div>
                    </div>
                  </li>
                  <li>
                    <div className="flex items-baseline gap-2 sm:gap-3">
                      <div className="text-xl sm:text-3xl font-bold text-gray-900">5 hours</div>
                      <div className="text-gray-500 text-xs sm:text-sm">Production Time</div>
                    </div>
                  </li>
                  <li>
                    <div className="flex items-baseline gap-2 sm:gap-3">
                      <div className="text-xl sm:text-3xl font-bold text-gray-900">3-4</div>
                      <div className="text-gray-500 text-xs sm:text-sm">Team Members</div>
                    </div>
                  </li>
                  <li>
                    <div className="flex items-baseline gap-2 sm:gap-3">
                      <div className="text-xl sm:text-3xl font-bold text-gray-900">Expert</div>
                      <div className="text-gray-500 text-xs sm:text-sm">Technical Skills</div>
                    </div>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* SmartReel Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="relative overflow-hidden rounded-xl bg-indigo-50/50 p-5 sm:p-8 shadow-sm border border-indigo-100"
            >
              <div className="absolute top-0 right-0 w-[200px] h-[200px] bg-indigo-100 rounded-full blur-3xl -mr-20 -mt-20 opacity-30" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-4 sm:mb-8">
                  <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-indigo-600" />
                  <h3 className="text-base sm:text-xl font-semibold text-indigo-900">SmartReel</h3>
                </div>
                <ul className="space-y-3 sm:space-y-6">
                  <li>
                    <div className="flex items-baseline gap-2 sm:gap-3">
                      <div className="text-xl sm:text-3xl font-bold text-indigo-900">$0</div>
                      <div className="text-indigo-900/70 text-xs sm:text-sm">Equipment & Hiring</div>
                    </div>
                  </li>
                  <li>
                    <div className="flex items-baseline gap-2 sm:gap-3">
                      <div className="text-xl sm:text-3xl font-bold text-indigo-900">5 min</div>
                      <div className="text-indigo-900/70 text-xs sm:text-sm">Production Time</div>
                    </div>
                  </li>
                  <li>
                    <div className="flex items-baseline gap-2 sm:gap-3">
                      <div className="text-xl sm:text-3xl font-bold text-indigo-900">Just You</div>
                      <div className="text-indigo-900/70 text-xs sm:text-sm">Team Members</div>
                    </div>
                  </li>
                  <li>
                    <div className="flex items-baseline gap-2 sm:gap-3">
                      <div className="text-xl sm:text-3xl font-bold text-indigo-900">None</div>
                      <div className="text-indigo-900/70 text-xs sm:text-sm">Technical Skills</div>
                    </div>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>

        </div>
      </section>

      {/* Mobile-optimized floating CTA */}
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ 
          opacity: showFloatingCTA ? 1 : 0,
          y: showFloatingCTA ? 0 : 100
        }}
        transition={{ duration: 0.2 }}
        className={`fixed bottom-0 left-0 right-0 md:bottom-6 md:right-6 md:left-auto z-50 p-4 md:p-0 bg-gradient-to-t from-white via-white to-transparent md:bg-none ${
          !showFloatingCTA ? 'pointer-events-none' : ''
        }`}
      >
        <Button
          size="lg"
          className="w-full md:w-auto flex items-center justify-center px-6 py-5 md:py-6 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-xl shadow-amber-600/20 rounded-xl md:rounded-full transition-all"
          onClick={() => navigate('/auth')}
        >
          <div className="flex items-center gap-3">
            <Gift className="h-5 w-5" />
            <div>
              <div className="text-xs font-medium text-amber-100">Limited Time Gift</div>
              <div className="text-[1.0625rem] font-semibold leading-snug">Claim 500 Credits Now</div>
            </div>
          </div>
        </Button>
      </motion.div>

      {/* Features Section - Mobile Optimized */}
      <section className="py-12 sm:py-16 md:py-20 bg-white overflow-x-hidden">
        <div className="container mx-auto px-4 sm:px-5 md:px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-10 sm:mb-14 md:mb-12"
          >
            <h2 className="text-[1.75rem] sm:text-2xl md:text-3xl font-bold mb-4">The SmartReel Advantage</h2>
            <p className="text-base md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Everything you need to create professional videos without the traditional hassle
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-6 lg:gap-y-10 max-w-5xl mx-auto">
            {advantages.map(a => (
              <div
                key={a.title}
                className="flex items-start gap-4 text-left lg:block lg:px-2 lg:text-center"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-indigo-100 bg-indigo-50 text-indigo-600 lg:mx-auto lg:mb-4 lg:h-14 lg:w-14">
                  <a.icon className="h-5 w-5 lg:h-6 lg:w-6" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 lg:text-lg">{a.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-gray-500 lg:mx-auto lg:mt-1.5 lg:max-w-[15rem]">
                    {a.blurb}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer ref={footerRef} className="bg-white border-t w-full">
        <div className="container mx-auto px-4 sm:px-5 md:px-4 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0">
            <p className="text-[0.9375rem] text-gray-600 order-2 md:order-1">
              © 2026 SmartReel.
            </p>
            <div className="text-[0.9375rem] text-gray-600 order-1 md:order-2">
              Built by{' '}
              <a 
                href="https://adeystudios.com/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="font-medium hover:text-indigo-600 transition-colors"
              >
                AdeyStudios
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="p-5 md:p-6 rounded-lg border bg-white shadow-sm"
    >
      <div className="mb-4">{icon}</div>
      <h3 className="text-lg md:text-xl font-semibold mb-2">{title}</h3>
      <p className="text-sm md:text-base text-gray-600 leading-relaxed">{description}</p>
    </motion.div>
  );
}

function Step({ number, title, description }: {
  number: number;
  title: string;
  description: React.ReactNode;
}) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-medium text-[0.9375rem] md:text-base">
        {number}
      </div>
      <div>
        <h3 className="text-[1.125rem] md:text-xl font-semibold mb-3">{title}</h3>
        <div className="text-[0.9375rem] md:text-base text-gray-600 leading-relaxed">{description}</div>
      </div>
    </div>
  );
}